import React, { useState, useEffect } from 'react';
import Modal from '../Modal/Modal';
import SlideCaptcha, { CaptchaData } from './SlideCaptcha';
import { CaptchaRequiredResponse } from '../../types/captcha';
import {
  ModalContainer,
  ModalBody,
} from './SlideCaptchaModal.styles';

export interface SlideCaptchaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOverlayClose?: () => void; // New prop for overlay-specific close behavior
  onSuccess: (captcha_key: string, slide_x: number, slide_y: number) => Promise<boolean>;
  captchaData: CaptchaRequiredResponse | null;
  error?: string;
}

// Convert API response to component format  
const convertCaptchaData = (apiData: Extract<CaptchaRequiredResponse, { captcha_required: true }>['data']): CaptchaData => {
  return {
    key: apiData.captcha_key,
    image: apiData.master_image_base64,
    thumb: apiData.thumb_image_base64,
    thumbX: apiData.display_x,
    thumbY: apiData.display_y,
    thumbWidth: apiData.thumb_width,
    thumbHeight: apiData.thumb_height,
  };
};

const SlideCaptchaModal: React.FC<SlideCaptchaModalProps> = ({
  isOpen,
  onClose,
  onOverlayClose,
  onSuccess,
  captchaData,
  error
}) => {
  const [loading, setLoading] = useState(false);


  const handleValidate = async (point: { x: number; y: number }, key: string, duration: number): Promise<boolean> => {
    // Convert point coordinates back to slide position
    // Since we added thumbX to get the point, subtract it to get slide position
    const convertedCaptchaData = captchaData && captchaData.captcha_required ? convertCaptchaData(captchaData.data) : null;
    
    // Compute slide coordinates, clamping to 0 and rounding to integers
    const slide_x = Math.round(Math.max(0, point.x - (convertedCaptchaData?.thumbX || 0)));
    const slide_y = Math.round(Math.max(0, convertedCaptchaData?.thumbY || 0));
    
    setLoading(true);
    
    try {
      const isValid = await onSuccess(key, slide_x, slide_y);
      return isValid;
    } catch (err) {
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    // Refresh will be handled by parent component
    onClose();
  };

  // Reset loading state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setLoading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onOverlayClose || onClose}>
      <ModalContainer>

        <ModalBody>

          <SlideCaptcha
            data={captchaData && captchaData.captcha_required ? convertCaptchaData(captchaData.data) : null}
            onValidate={handleValidate}
            onRefresh={handleRefresh}
            loading={loading}
            error={error}
          />
        </ModalBody>

      </ModalContainer>
    </Modal>
  );
};

export default SlideCaptchaModal;